import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { blogPosts } from '@/data/blogPosts';
import { ArrowRight, Clock, Tag } from 'lucide-react';

export default function Blog() {
  useSEO({
    title: 'Tax Tips & Insights Blog',
    description: 'Practical tax tips, IRS guidance, and expert insights from TaxPro - Answers. Learn how to handle IRS notices, deductions, unfiled returns, and more.',
  });

  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Tax Tips & Insights
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Practical Tax Guidance You Can Use
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Clear, plain-English articles about common tax problems, IRS notices, deductions, and when to seek professional help.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <Link
              to={`/blog/${featured.id}`}
              data-testid="blog-featured-post"
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 group"
            >
              <div className="rounded-lg overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-64 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-green">
                    <Tag className="w-3 h-3" /> {featured.category}
                  </span>
                  <span className="text-xs text-slate-400">{featured.date}</span>
                  <span className="flex items-center gap-1 text-xs text-slate-400">
                    <Clock className="w-3 h-3" /> {featured.readTime}
                  </span>
                </div>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-3 group-hover:text-brand-green transition-colors">
                  {featured.title}
                </h2>
                <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-5">
                  {featured.description}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green">
                  Read Article <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* All Posts Grid */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-12">
              More Articles
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rest.map((post, i) => (
              <AnimatedSection key={post.id} delay={i * 0.08}>
                <Link
                  to={`/blog/${post.id}`}
                  data-testid={`blog-card-${post.id}`}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-brand-green/40 transition-colors group flex flex-col h-full"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-widest text-brand-green">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock className="w-3 h-3" /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-brand-navy mb-2 group-hover:text-brand-green transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-4 flex-1">
                      {post.description}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-green opacity-0 group-hover:opacity-100 transition-opacity">
                      Read more <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Have a tax question of your own?
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Reading articles is a great start. When you need personalized guidance, we're here to help.
            </p>
            <Link to="/ask">
              <Button
                data-testid="blog-cta-ask"
                className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
              >
                Ask a Tax Question - Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
